import { useApolloClient } from "@apollo/client";
import { Col, Row } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { useQuery } from "shared/hooks/useApi";
import { fetchZipCodeCities } from "shared/hooks/useZipCodeCities";
import { NAME_SPACES } from "shared/locales/constants";
import { INSURANCE_PLANS } from "utils/api/graphql/queries/insurance-plans";
import {
  CLIENT_CATEGORY,
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
  LMF_SOCIAL_REGIMES,
  PROFILE_LIST,
} from "utils/constants";
import { getMaxDeltaEnd, getMinDeltaStart } from "utils/helpers/array";
import { hundredYearsAgoStart } from "utils/helpers/date";
import { bindDateInputProps, bindInputProps } from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import Actions from "../Components/Actions";
import Button from "../Components/Button";
import Head from "../Components/Head";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import Select from "../Components/Select";
import InputChecked from "../Components/svg/check_small.svg";
import InputError from "../Components/svg/error.svg";
import validation from "./validation";

const REGIMES_SWITCH_PROFILE = {
  [PROFILE_LIST.PARTICULAR]: [
    LMF_SOCIAL_REGIMES.SECURITE_SOCIALE,
    LMF_SOCIAL_REGIMES.ALSACE_MOSELLE,
    LMF_SOCIAL_REGIMES.MINIER,
    LMF_SOCIAL_REGIMES.REGIME_SALARIES_AGRICOLES,
  ],
  [PROFILE_LIST.TERRITORIAL_AGENT]: [
    LMF_SOCIAL_REGIMES.SECURITE_SOCIALE,
    LMF_SOCIAL_REGIMES.ALSACE_MOSELLE,
  ],
  [PROFILE_LIST.TNS]: [
    LMF_SOCIAL_REGIMES.SECURITE_SOCIALE,
    LMF_SOCIAL_REGIMES.ALSACE_MOSELLE,
  ],
  [PROFILE_LIST.ASSOCIATION_MEMBER]: [
    LMF_SOCIAL_REGIMES.SECURITE_SOCIALE,
    LMF_SOCIAL_REGIMES.ALSACE_MOSELLE,
  ],
  [PROFILE_LIST.C2SP]: [
    LMF_SOCIAL_REGIMES.SECURITE_SOCIALE,
    LMF_SOCIAL_REGIMES.ALSACE_MOSELLE,
  ],
  [PROFILE_LIST.C2S_EXIT_CONTRACT]: [
    LMF_SOCIAL_REGIMES.SECURITE_SOCIALE,
    LMF_SOCIAL_REGIMES.ALSACE_MOSELLE,
  ],
  [PROFILE_LIST.THIS_FAMILY]: [
    LMF_SOCIAL_REGIMES.SECURITE_SOCIALE,
    LMF_SOCIAL_REGIMES.ALSACE_MOSELLE,
  ],
};

const View = ({ onNext, initialValues, locked, onBack }) => {
  const client = useApolloClient();
  const [deltaRia, setDeltaRia] = useState(30);
  const [deltaStart, setDeltaStart] = useState(1);
  const [deltaEndRia, setDeltaEndRia] = useState();
  const [deltaEnd, setDeltaEnd] = useState();
  const [open, setOpen] = useState(false);

  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("B2C_LMF.CONTACT", { returnObjects: true });

  useQuery(INSURANCE_PLANS, {
    variables: { where: {} },
    onCompleted: ({ insurancePlans: { data: insurancePlans } }) => {
      const deltaRia = getMinDeltaStart({ insurancePlans, minValue: 30 });
      const deltaStart = getMinDeltaStart({
        insurancePlans,
        minValue: 1,
        key: "DELTA_START",
      });
      const deltaEndRia = getMaxDeltaEnd({ insurancePlans }) || 0;
      const deltaEnd =
        getMaxDeltaEnd({
          insurancePlans,
          key: "DELTA_END",
        }) || 0;
      setDeltaRia(deltaRia);
      setDeltaStart(deltaStart);
      setDeltaEndRia(deltaEndRia);
      setDeltaEnd(deltaEnd);
    },
  });

  const isParticular = useMemo(
    () =>
      get(initialValues, "contact.fields.profile") ===
      CLIENT_CATEGORY.PARTICULAR,
    [],
  );

  const { handleSubmit, values, setFieldValue, ...formProps } = useFormik({
    initialValues,
    onSubmit: onNext,
    enableReinitialize: true,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      {
        fetchZipCodeCities: (zipCode) => fetchZipCodeCities(client, zipCode),
        deltaStart,
        deltaEnd,
        deltaRia,
        deltaEndRia,
      },
    ),
  });

  useEffect(() => {
    const currentProfile = get(values, "contact.fields.profile");
    const currentRegime = get(values, "contact.socialRegime");

    if (currentProfile && currentRegime) {
      const availableRegimes = get(
        REGIMES_SWITCH_PROFILE,
        currentProfile,
        Object.values(LMF_SOCIAL_REGIMES),
      );

      if (!availableRegimes.includes(currentRegime)) {
        setFieldValue("contact.socialRegime", undefined);

        if (availableRegimes.length === 1) {
          setFieldValue("contact.socialRegime", availableRegimes[0]);
        }
      }
    }
  }, []);

  const pickerValue = !get(values, "project.contract.issuanceDate") && dayjs();

  const isMobile = useMediaQuery({ maxWidth: 768 });

  return (
    <>
      {/* <CustomModal
        open={open}
        setOpen={setOpen}
        title={TRANSLATION.HELP_MODAL.TITLE}
        description={TRANSLATION.HELP_MODAL.DESCRIPTION}
        icon={<HelpYellow size={64} />}
        TRANSLATION={TRANSLATION}
        width={isMobile ? "90%" : 500}
        closeText={TRANSLATION.HELP_MODAL.CLOSE}
      >
      </CustomModal> */}

      <Layout>
        <Head
          description={TRANSLATION.DESCRIPTION}
          title={TRANSLATION.TITLE}
          onBack={onBack}
        />
        <div className="__LMF--content LMF-content lmf__form">
          <Row justify="center" gutter={[16, 16]}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Input
                label={TRANSLATION.FORM.ZIP_CODE.LABEL}
                placeholder={TRANSLATION.FORM.ZIP_CODE.PLACEHOLDER}
                className={classNames({
                  valid: get(
                    values,
                    `contact.telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                  ),
                })}
                inputMode="numeric"
                {...bindInputProps({
                  name: `contact.telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                  values,
                  setFieldValue,
                  ...formProps,
                })}
                onChange={async ({ target: { value } }) => {
                  if (value.length > 5) return;
                  setFieldValue(
                    `contact.telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                    value.toString(),
                  );
                  if (value?.length >= 2)
                    setFieldValue(
                      "contact.fields.department",
                      value.toString().substring(0, 2),
                    );
                }}
                disabled={locked}
                suffix={
                  get(
                    formProps.errors,
                    `contact.telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                  ) ? (
                    <img src={InputError} alt="" />
                  ) : (
                    <img src={InputChecked} alt="" />
                  )
                }
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Input
                label={TRANSLATION.FORM.BIRTH_DATE.LABEL}
                placeholder={TRANSLATION.FORM.BIRTH_DATE.PLACEHOLDER}
                className={classNames({
                  valid: get(values, "contact.user.birthDate"),
                })}
                type="date"
                variant="simple"
                format={DATE_FORMAT}
                disabledDate={(current) =>
                  current > dayjs().subtract(18, "years").endOf("year") ||
                  current < hundredYearsAgoStart
                }
                defaultPickerValue={
                  !get(values, "contact.user.birthDate") &&
                  dayjs().subtract(30, "years")
                }
                {...bindDateInputProps({
                  name: "contact.user.birthDate",
                  values,
                  setFieldValue,
                  ...formProps,
                })}
                disabled={locked}
                // right={<HelpYellow size={24} onClick={() => setOpen(true)} />}
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Select
                options={get(
                  REGIMES_SWITCH_PROFILE,
                  get(values, "contact.fields.profile"),
                  Object.values(LMF_SOCIAL_REGIMES),
                ).map((value) => {
                  const key = Object.keys(LMF_SOCIAL_REGIMES).find(
                    (k) => LMF_SOCIAL_REGIMES[k] === value,
                  );
                  return {
                    value,
                    label: TRANSLATION.FORM.SOCIAL_REGIMES[key],
                  };
                })}
                className={classNames({
                  valid: get(values, "contact.socialRegime"),
                })}
                placeholder={TRANSLATION.FORM.SOCIAL_REGIME.PLACEHOLDER}
                label={TRANSLATION.FORM.SOCIAL_REGIME.LABEL}
                {...bindInputProps({
                  name: "contact.socialRegime",
                  values,
                  setFieldValue,
                  ...formProps,
                })}
                onChange={(value) => {
                  setFieldValue("contact.socialRegime", value);
                }}
                disabled={locked}
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Input
                type="date"
                variant="simple"
                className={classNames({
                  valid: get(values, "contract.issuanceDate"),
                })}
                label={TRANSLATION.FORM.EFFECTIVE_DATE.LABEL}
                defaultPickerValue={pickerValue}
                placeholder={TRANSLATION.FORM.EFFECTIVE_DATE.PLACEHOLDER}
                disabledDate={(current) => {
                  const validEndDate = values.ria
                    ? deltaEndRia === 0
                      ? dayjs().endOf("year")
                      : dayjs().add(deltaEndRia, "days")
                    : deltaEnd === 0
                      ? dayjs().endOf("year")
                      : dayjs().add(deltaEnd, "days");
                  const validStartDate = values.ria
                    ? dayjs(
                        moment
                          .max(
                            moment().add(deltaRia, "days").startOf("day"),
                            moment().startOf("y"),
                          )
                          .toDate(),
                      )
                    : dayjs(
                        moment
                          .max(
                            moment().add(deltaStart, "days").startOf("day"),
                            moment().startOf("y"),
                          )
                          .toDate(),
                      );
                  return current && current < validStartDate;
                }}
                {...bindDateInputProps({
                  name: "contract.issuanceDate",
                  values,
                  setFieldValue,
                  ...formProps,
                })}
                format={DATE_FORMAT}
                disabled={locked}
              />
            </Col>

            <Col xs={24} sm={24} md={24} lg={24}>
              {/* <div className="mt-20" /> */}
              <Actions
                variant={"light"}
                label={TRANSLATION.FORM.ALREADY_ADHERENT}
                actions={
                  <>
                    <Button
                      size="small"
                      onClick={() => {
                        onNext({
                          contact: {
                            ...get(initialValues, "contact"),
                            fields: {
                              ...get(initialValues, "contact.fields"),
                              isClient: true,
                            },
                          },
                        });
                      }}
                      type={classNames({
                        primary:
                          get(values, "contact.fields.isClient") === true,
                        white: true,
                      })}
                    >
                      {TRANSLATION.FORM.YES}
                    </Button>
                    <Button
                      size="small"
                      onClick={() =>
                        setFieldValue("contact.fields.isClient", false)
                      }
                      type={classNames({
                        primary:
                          get(values, "contact.fields.isClient") === false,
                        white: get(values, "contact.fields.isClient") !== false,
                      })}
                    >
                      {TRANSLATION.FORM.NO}
                    </Button>
                  </>
                }
              />
            </Col>
            {isParticular && (
              <Col xs={24} sm={24} md={24} lg={24}>
                <Actions
                  variant={"light"}
                  label={TRANSLATION.FORM.CSS}
                  actions={
                    <>
                      <Button
                        size="small"
                        onClick={() => {
                          onNext({
                            ...values,
                            fields: {
                              ...get(values, "fields", {}),
                              info: {
                                ...get(values, "fields.info", {}),
                                cssBeneficiary: true,
                              },
                            },
                          });
                        }}
                        type={classNames({
                          primary:
                            get(values, "fields.info.cssBeneficiary") === true,
                          white:
                            get(values, "fields.info.cssBeneficiary") !== true,
                        })}
                      >
                        {TRANSLATION.FORM.YES}
                      </Button>
                      <Button
                        size="small"
                        onClick={() =>
                          setFieldValue("fields.info.cssBeneficiary", false)
                        }
                        type={classNames({
                          primary:
                            get(values, "fields.info.cssBeneficiary") === false,
                          white:
                            get(values, "fields.info.cssBeneficiary") !== false,
                        })}
                      >
                        {TRANSLATION.FORM.NO}
                      </Button>
                    </>
                  }
                />
              </Col>
            )}
          </Row>
        </div>

        <ActionButtons
          disabled={!isEmpty(formProps.errors)}
          onNext={handleSubmit}
          buttonText={TRANSLATION.NEXT}
          {...(isMobile && { onBack })}
          className={classNames({
            "w-100": isMobile,
          })}
        />
      </Layout>
    </>
  );
};

export default View;
